import type { UseQueryOptions } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KEYS, repository } from '@repositories/Repository';
import type IScimTokenRepository from '@repositories/scimTokenRepository/IScimTokenRepository';
import { ScimTokenModel } from '@repositories/scimTokenRepository/models/ScimTokenModel';
import type { ScimTokenDTO, ScimTokenCreateDTO } from '@repositories/scimTokenRepository/scimToken.types';

type CreateScimTokenData = {
  tenantId: string;
  dto: ScimTokenCreateDTO;
};

type DeleteScimTokenData = {
  tenantId: string;
  tokenId: string;
};

type RevokeScimTokenData = {
  tenantId: string;
  tokenId: string;
};

type ActivateScimTokenData = {
  tenantId: string;
  tokenId: string;
};

const scimTokenRepo = repository.get<IScimTokenRepository>(KEYS.SCIM_TOKEN_REPOSITORY);

const scimTokenKey = {
  all: ['scimToken'] as const,
  lists: () => [...scimTokenKey.all, 'lists'] as const,
  list: (tenantId: string) => [...scimTokenKey.lists(), tenantId] as const,
  details: () => [...scimTokenKey.all, 'detail'] as const,
  detail: (tenantId: string, tokenId: string) => [...scimTokenKey.details(), tenantId, tokenId] as const,
};

// SCIM 토큰 목록 조회
export const useGetScimTokens = (
  tenantId: string,
  options?: Omit<UseQueryOptions<ScimTokenDTO[], AxiosError, ScimTokenModel[]>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectScimTokenList = useCallback((data: ScimTokenDTO[]) => data.map(dto => new ScimTokenModel(dto)), []);

  return useQuery<ScimTokenDTO[], AxiosError, ScimTokenModel[]>({
    queryKey: scimTokenKey.list(tenantId),
    queryFn: () => scimTokenRepo.getScimTokens(tenantId),
    select: selectScimTokenList,
    ...options,
    enabled: !!tenantId && (options?.enabled ?? true),
  });
};

// SCIM 토큰 상세 조회
export const useGetScimToken = (
  tenantId: string,
  tokenId: string,
  options?: Omit<UseQueryOptions<ScimTokenDTO, AxiosError, ScimTokenModel>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  const selectScimToken = useCallback((data: ScimTokenDTO) => new ScimTokenModel(data), []);

  return useQuery<ScimTokenDTO, AxiosError, ScimTokenModel>({
    queryKey: scimTokenKey.detail(tenantId, tokenId),
    queryFn: () => scimTokenRepo.getScimToken(tenantId, tokenId),
    select: selectScimToken,
    ...options,
    enabled: !!tenantId && !!tokenId && (options?.enabled ?? true),
  });
};

// SCIM 토큰 생성
export const useCreateScimToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateScimTokenData) => {
      const result = await scimTokenRepo.createScimToken(data.tenantId, data.dto);
      return new ScimTokenModel(result);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scimTokenKey.lists() });
    },
  });
};

// SCIM 토큰 삭제
export const useDeleteScimToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: DeleteScimTokenData) => {
      await scimTokenRepo.deleteScimToken(data.tenantId, data.tokenId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scimTokenKey.all });
    },
  });
};

// SCIM 토큰 비활성화
export const useRevokeScimToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: RevokeScimTokenData) => {
      await scimTokenRepo.revokeScimToken(data.tenantId, data.tokenId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scimTokenKey.all });
    },
  });
};

// SCIM 토큰 활성화
export const useActivateScimToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ActivateScimTokenData) => {
      await scimTokenRepo.activateScimToken(data.tenantId, data.tokenId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scimTokenKey.all });
    },
  });
};
